import { Controller } from "@hotwired/stimulus"
import { apiGet, apiPost, apiDelete, formatMoney, t } from "../api_helper"
import { icon } from "../lib/icons"
import { networkStatus } from "../lib/network_status"
import { isOfflineError, showOfflinePlaceholder, alertOffline } from "../lib/offline_ui"
import "../lib/paywall"

export default class extends Controller {
  static targets = ["list", "form", "addBtn", "name", "targetAmount", "targetDate", "icon", "color", "currency"]

  connect() {
    this.load()
  }

  async load() {
    try {
      const data = await apiGet("/api/goals")
      this.goals = data.goals || []
      this.renderList()
    } catch (e) {
      if (isOfflineError(e)) { showOfflinePlaceholder(this.hasListTarget ? this.listTarget : null, () => this.load()); return }
      if (e.status === 403) window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "goals" } }))
      else console.error("Goals load error:", e)
    }
  }

  renderList() {
    if (!this.hasListTarget) return

    if (!this.goals.length) {
      this.listTarget.innerHTML = `
        <div class="text-center py-8 px-4 rounded-2xl" style="background-color: rgb(248,250,252);">
          <span class="block mb-2">${icon("target", "w-10 h-10 text-slate-300")}</span>
          <div class="text-sm" style="color: rgb(148,163,184);">${t("goals.no_goals") || "No goals yet"}</div>
        </div>`
      return
    }

    const html = this.goals.map(g => {
      const pct = g.percentage
      const barColor = g.completed ? "rgb(16,185,129)" : (g.color || "rgb(99,102,241)")
      const statusText = g.completed
        ? `<span style="color: rgb(16,185,129);">&#10003; ${t("goals.completed") || "Completed!"}</span>`
        : `${formatMoney(g.remaining, g.currency)} ${t("goals.remaining") || "remaining"}`

      return `
        <div class="p-4 rounded-2xl mb-2" style="background-color: rgb(248,250,252);">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2">
              <span style="font-size: 20px;">${g.icon}</span>
              <span class="font-semibold text-sm">${g.name}</span>
            </div>
            <button data-action="click->pwa-goals#delete" data-id="${g.id}"
                    class="text-xs px-2 py-1 rounded-lg active:scale-[0.98] transition-transform"
                    style="color: rgb(148,163,184);">&times;</button>
          </div>

          <div style="height: 8px; border-radius: 4px; background-color: rgb(241,245,249); overflow: hidden; margin-bottom: 6px;">
            <div style="height: 100%; width: ${pct}%; border-radius: 4px; background-color: ${barColor}; transition: width 0.3s;"></div>
          </div>

          <div class="flex justify-between items-center text-xs">
            <span style="color: rgb(148,163,184);">${formatMoney(g.current_amount, g.currency)} / ${formatMoney(g.target_amount, g.currency)}</span>
            <span class="font-medium" style="color: ${g.completed ? "rgb(16,185,129)" : "inherit"};">${pct}%</span>
          </div>

          <div class="text-xs mt-1" style="color: rgb(148,163,184);">${statusText}</div>

          ${!g.completed ? `
            <div class="mt-3">
              <input type="number" data-fund-input="${g.id}" step="0.01" min="0.01"
                     placeholder="${t("goals.fund_amount_placeholder") || "Amount"}"
                     class="w-full p-2 rounded-lg text-sm mb-2"
                     style="background-color: rgb(241,245,249); color: inherit; border: 1px solid transparent; outline: none;">
              <div class="flex gap-2">
                <button data-action="click->pwa-goals#addFunds" data-id="${g.id}"
                        class="flex-1 py-2 rounded-lg text-xs font-semibold active:scale-[0.98] transition-transform"
                        style="background-color: rgba(16,185,129,0.15); color: rgb(16,185,129);">
                  +${t("goals.add_funds") || "Add"}
                </button>
                <button data-action="click->pwa-goals#withdrawFunds" data-id="${g.id}"
                        class="flex-1 py-2 rounded-lg text-xs font-semibold active:scale-[0.98] transition-transform"
                        style="background-color: rgba(239,68,68,0.1); color: rgb(239,68,68);">
                  -${t("goals.withdraw") || "Withdraw"}
                </button>
              </div>
            </div>` : ""}

          ${g.target_date ? `<div class="text-xs mt-1 flex items-center gap-1" style="color: rgb(148,163,184);">${icon("clock", "w-3 h-3 inline")} ${g.target_date}</div>` : ""}
        </div>`
    }).join("")

    this.listTarget.innerHTML = `<div class="fade-in">${html}</div>`
  }

  showForm() {
    if (this.hasFormTarget) this.formTarget.classList.remove("hidden")
    if (this.hasAddBtnTarget) this.addBtnTarget.classList.add("hidden")
  }

  cancelForm() {
    if (this.hasFormTarget) this.formTarget.classList.add("hidden")
    if (this.hasAddBtnTarget) this.addBtnTarget.classList.remove("hidden")
  }

  async save() {
    if (!networkStatus.online) { alertOffline(); return }
    const name = this.nameTarget?.value?.trim()
    const target = parseFloat(this.targetAmountTarget?.value)
    if (!name || !target || target <= 0) return

    try {
      await apiPost("/api/goals", {
        goal: {
          name,
          target_amount: target,
          target_date: this.targetDateTarget?.value || null,
          icon: this.iconTarget?.value || "",
          color: this.colorTarget?.value || "#3B82F6",
          currency: this.hasCurrencyTarget ? this.currencyTarget.value : null
        }
      })
      this.cancelForm()
      this.nameTarget.value = ""
      this.targetAmountTarget.value = ""
      this.targetDateTarget.value = ""
      this.load()
    } catch (e) {
      if (e.status === 403) window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "goals" } }))
      else console.error("Save goal error:", e)
    }
  }

  async addFunds(event) {
    if (!networkStatus.online) { alertOffline(); return }
    const id = event.currentTarget.dataset.id
    const input = this.element.querySelector(`[data-fund-input="${id}"]`)
    const amount = parseFloat(input?.value)
    if (!amount || amount <= 0) {
      if (input) {
        input.style.outline = "2px solid rgb(239,68,68)"
        setTimeout(() => { input.style.outline = "none" }, 1500)
      }
      return
    }

    try {
      await apiPost(`/api/goals/${id}/add_funds`, { amount })
      this.load()
    } catch (e) {
      console.error("Add funds error:", e)
    }
  }

  async withdrawFunds(event) {
    if (!networkStatus.online) { alertOffline(); return }
    const id = event.currentTarget.dataset.id
    const input = this.element.querySelector(`[data-fund-input="${id}"]`)
    const amount = parseFloat(input?.value)
    if (!amount || amount <= 0) {
      if (input) {
        input.style.outline = "2px solid rgb(239,68,68)"
        setTimeout(() => { input.style.outline = "none" }, 1500)
      }
      return
    }

    try {
      await apiPost(`/api/goals/${id}/withdraw_funds`, { amount })
      this.load()
    } catch (e) {
      console.error("Withdraw funds error:", e)
    }
  }

  async delete(event) {
    if (!networkStatus.online) { alertOffline(); return }
    const id = event.currentTarget.dataset.id
    if (!confirm(t("goals.delete_confirm") || "Delete?")) return

    try {
      await apiDelete(`/api/goals/${id}`)
      this.load()
    } catch (e) {
      console.error("Delete goal error:", e)
    }
  }
}
